// Stadium: night sky, striped pitch with markings, goal + deformable box net, LED boards,
// stands with an instanced animated crowd, floodlight towers and the scene lighting.
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { GOAL, BOARD_Z, SPOT_Z } from './config.js';
import { grassTexture, netTexture, boardTexture, glowTexture, lampTexture, rng } from './textures.js';

const PX = GOAL.halfWidth + GOAL.postR;
const BAR_Y = GOAL.height + GOAL.postR;

export function createStadium(scene, { quality = 'high' } = {}) {
  const disposables = [];
  const track = (o) => { disposables.push(o); return o; };
  const root = new THREE.Group();
  scene.add(root);

  // ---------- sky ----------
  const skyMat = track(new THREE.ShaderMaterial({
    side: THREE.BackSide,
    depthWrite: false,
    fog: false,
    uniforms: { top: { value: new THREE.Color('#04070f') }, mid: { value: new THREE.Color('#13224a') }, glow: { value: new THREE.Color('#4a66a8') } },
    vertexShader: 'varying vec3 vP; void main(){ vP = normalize(position); gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }',
    fragmentShader: `uniform vec3 top; uniform vec3 mid; uniform vec3 glow; varying vec3 vP;
      float h(vec3 p){ return fract(sin(dot(p, vec3(12.9898,78.233,45.164))) * 43758.5453); }
      void main(){
        float y = vP.y;
        vec3 c = mix(mid, top, smoothstep(0.02, 0.55, y));
        c += glow * pow(max(0.0, 1.0 - abs(y - 0.12) * 3.0), 2.0) * 0.7;
        vec3 q = floor(vP * 420.0);
        float s = step(0.9975, h(q)) * smoothstep(0.15, 0.5, y);
        c += vec3(s) * 0.7;
        gl_FragColor = vec4(c, 1.0);
      }`,
  }));
  const sky = new THREE.Mesh(track(new THREE.SphereGeometry(450, 32, 16)), skyMat);
  sky.renderOrder = -1;
  root.add(sky);

  // ---------- pitch ----------
  const grass = track(grassTexture());
  grass.repeat.set(70, 80);
  const pitchMat = track(new THREE.MeshStandardMaterial({ map: grass, roughness: 0.96, metalness: 0, color: '#ffffff' }));
  pitchMat.onBeforeCompile = (shader) => {
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vWPos;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvWPos = (modelMatrix * vec4(transformed, 1.0)).xyz;');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vWPos;')
      .replace('#include <map_fragment>', `#include <map_fragment>
        float inPitch = step(abs(vWPos.x), 34.0) * step(-0.5, vWPos.z);
        float band = step(0.5, fract(vWPos.z / 11.0));
        float cross = step(0.5, fract((vWPos.x + 34.0) / 11.33));
        float stripe = mix(0.82, 1.06, band) * mix(0.97, 1.02, cross);
        diffuseColor.rgb *= mix(vec3(0.72, 0.82, 0.72), vec3(stripe), inPitch);
        // wear around the penalty spot and in the goalmouth
        float spotWear = exp(-pow(length(vWPos.xz - vec2(0.0, ${SPOT_Z.toFixed(1)})) / 1.1, 2.0));
        float mouthWear = exp(-pow(vWPos.x / 2.2, 2.0) - pow((vWPos.z - 0.9) / 0.9, 2.0));
        diffuseColor.rgb = mix(diffuseColor.rgb, vec3(0.36, 0.33, 0.2), clamp(spotWear * 0.55 + mouthWear * 0.45, 0.0, 0.7));`);
  };
  const pitch = new THREE.Mesh(track(new THREE.PlaneGeometry(140, 160)), pitchMat);
  pitch.rotation.x = -Math.PI / 2;
  pitch.position.set(0, 0, 50);
  pitch.receiveShadow = true;
  root.add(pitch);

  // ---------- line markings (one merged mesh) ----------
  const lineGeo = track(buildPitchLines());
  const lineMat = track(new THREE.MeshStandardMaterial({ color: '#f4f7f2', roughness: 0.8, emissive: '#202420', polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 }));
  const lines = new THREE.Mesh(lineGeo, lineMat);
  lines.receiveShadow = true;
  root.add(lines);

  // ---------- goal ----------
  const goal = buildGoal(track);
  root.add(goal.group);

  // ---------- advertising boards ----------
  const boardTex = track(boardTexture());
  boardTex.repeat.set(3, 1);
  const boardMat = track(new THREE.MeshBasicMaterial({ map: boardTex, toneMapped: false }));
  const boardBack = track(new THREE.MeshStandardMaterial({ color: '#15171d', roughness: 0.6 }));
  const boards = new THREE.Group();
  const boardGeo = track(new THREE.BoxGeometry(80, 0.95, 0.12));
  const board = new THREE.Mesh(boardGeo, [boardBack, boardBack, boardBack, boardBack, boardMat, boardBack]);
  board.position.set(0, 0.475, BOARD_Z);
  boards.add(board);
  for (const s of [-1, 1]) {
    // side boards along the touchlines near the goal
    const side = new THREE.Mesh(track(new THREE.BoxGeometry(40, 0.95, 0.12)), [boardBack, boardBack, boardBack, boardBack, boardMat, boardBack]);
    side.rotation.y = -s * Math.PI / 2;
    side.position.set(s * 37.5, 0.475, 14);
    boards.add(side);
  }
  root.add(boards);

  // ---------- stands + crowd ----------
  const stands = buildStands(track, quality);
  root.add(stands.group);

  // ---------- floodlights ----------
  const glowTex = track(glowTexture());
  const lampTex = track(lampTexture());
  const lampMat = track(new THREE.MeshBasicMaterial({ map: lampTex, toneMapped: false }));
  const steelMat = track(new THREE.MeshStandardMaterial({ color: '#4a4f5c', roughness: 0.5, metalness: 0.6 }));
  const flares = [];
  for (const [x, z] of [[-52, -30], [52, -30], [-56, 45], [56, 45]]) {
    const tower = new THREE.Group();
    const mast = new THREE.Mesh(track(new THREE.CylinderGeometry(0.5, 0.9, 40, 8)), steelMat);
    mast.position.y = 20;
    tower.add(mast);
    const head = new THREE.Mesh(track(new THREE.PlaneGeometry(9, 5.6)), lampMat);
    head.position.y = 41;
    tower.add(head);
    const frame = new THREE.Mesh(track(new THREE.BoxGeometry(9.4, 6, 0.4)), steelMat);
    frame.position.set(0, 41, -0.25);
    tower.add(frame);
    tower.position.set(x, 0, z);
    tower.lookAt(0, 0, 8);
    head.rotation.x = -0.35;
    frame.rotation.x = -0.35;
    const flare = new THREE.Sprite(track(new THREE.SpriteMaterial({ map: glowTex, color: '#fff4dc', blending: THREE.AdditiveBlending, depthWrite: false, transparent: true, fog: false })));
    flare.scale.set(30, 30, 1);
    flare.position.set(x, 41, z);
    flares.push(flare);
    root.add(tower, flare);
  }

  // ---------- lighting ----------
  const hemi = new THREE.HemisphereLight('#9fb4ff', '#1c3320', 0.85);
  root.add(hemi);
  const key = new THREE.DirectionalLight('#fff3e0', 2.6);
  key.position.set(-22, 38, 34);
  key.target.position.set(0, 0, 5);
  key.castShadow = true;
  key.shadow.mapSize.set(quality === 'low' ? 1024 : 2048, quality === 'low' ? 1024 : 2048);
  const sc = key.shadow.camera;
  sc.left = -11; sc.right = 11; sc.top = 14; sc.bottom = -8; sc.near = 10; sc.far = 90;
  key.shadow.bias = -0.0004;
  key.shadow.normalBias = 0.02;
  root.add(key, key.target);
  const fill = new THREE.DirectionalLight('#cfe0ff', 0.9);
  fill.position.set(26, 30, -20);
  root.add(fill);
  const rim = new THREE.DirectionalLight('#ffe6c4', 0.55);
  rim.position.set(0, 20, 60);
  root.add(rim);

  scene.fog = new THREE.Fog('#0d1529', 95, 330);

  let excitement = 0;
  return {
    root,
    nets: goal.nets,
    keyLight: key,
    update(t, dt) {
      boardTex.offset.x = (t * 0.035) % 1;
      excitement = Math.max(0, excitement - dt * 0.25);
      stands.uniforms.uTime.value = t;
      stands.uniforms.uExcite.value = excitement;
      goal.updateNets(dt);
    },
    cheer(amount = 1) { excitement = Math.min(1.2, excitement + amount); },
    setQuality(q) {
      stands.setDensity(q === 'low' ? 0.45 : 1);
      const size = q === 'low' ? 1024 : 2048;
      if (key.shadow.mapSize.x !== size) {
        key.shadow.mapSize.set(size, size);
        key.shadow.map?.dispose();
        key.shadow.map = null;
      }
    },
    dispose() {
      scene.remove(root);
      for (const d of disposables) d.dispose?.();
    },
  };
}

// ---------------------------------------------------------------------------

function buildPitchLines() {
  const W = 0.12;
  const Y = 0.008;
  const pos = [];
  const quad = (a, b, c, d) => pos.push(...a, ...b, ...c, ...a, ...c, ...d);
  // rectangle line from (x1,z1) to (x2,z2), axis aligned
  const seg = (x1, z1, x2, z2) => {
    const minx = Math.min(x1, x2) - (x1 === x2 ? W / 2 : 0);
    const maxx = Math.max(x1, x2) + (x1 === x2 ? W / 2 : 0);
    const minz = Math.min(z1, z2) - (z1 === z2 ? W / 2 : 0);
    const maxz = Math.max(z1, z2) + (z1 === z2 ? W / 2 : 0);
    quad([minx, Y, maxz], [maxx, Y, maxz], [maxx, Y, minz], [minx, Y, minz]);
  };
  const arc = (cx, cz, r, a0, a1, n = 48) => {
    for (let i = 0; i < n; i++) {
      const t0 = a0 + ((a1 - a0) * i) / n;
      const t1 = a0 + ((a1 - a0) * (i + 1)) / n;
      const p = (t, rr) => [cx + Math.sin(t) * rr, Y, cz + Math.cos(t) * rr];
      quad(p(t0, r - W / 2), p(t0, r + W / 2), p(t1, r + W / 2), p(t1, r - W / 2));
    }
  };
  const disc = (cx, cz, r, n = 20) => {
    for (let i = 0; i < n; i++) {
      const t0 = (i / n) * Math.PI * 2;
      const t1 = ((i + 1) / n) * Math.PI * 2;
      pos.push(cx, Y, cz, cx + Math.sin(t1) * r, Y, cz + Math.cos(t1) * r, cx + Math.sin(t0) * r, Y, cz + Math.cos(t0) * r);
    }
  };
  const HW = 34;
  const LEN = 105;
  seg(-HW, 0, HW, 0); // goal line
  seg(-HW, 0, -HW, LEN); seg(HW, 0, HW, LEN); // touchlines
  seg(-HW, LEN / 2, HW, LEN / 2); // halfway
  const six = GOAL.halfWidth + 5.5;
  seg(-six, 0, -six, 5.5); seg(six, 0, six, 5.5); seg(-six, 5.5, six, 5.5);
  const box = GOAL.halfWidth + 16.5;
  seg(-box, 0, -box, 16.5); seg(box, 0, box, 16.5); seg(-box, 16.5, box, 16.5);
  disc(0, SPOT_Z, 0.11);
  const dA = Math.acos(5.5 / 9.15);
  arc(0, SPOT_Z, 9.15, -dA, dA, 40);
  arc(0, LEN / 2, 9.15, 0, Math.PI * 2, 96);
  disc(0, LEN / 2, 0.15);
  arc(-HW, 0, 1, Math.PI / 2, 0, 12); // corner arcs
  arc(HW, 0, 1, 0, -Math.PI / 2, 12);
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  // all triangles must face up
  const p = g.attributes.position;
  for (let i = 0; i < p.count; i += 3) {
    const ax = p.getX(i), az = p.getZ(i), bx = p.getX(i + 1), bz = p.getZ(i + 1), cx = p.getX(i + 2), cz = p.getZ(i + 2);
    const ny = (bz - az) * (cx - ax) - (bx - ax) * (cz - az);
    if (ny < 0) { p.setXYZ(i + 1, cx, Y, cz); p.setXYZ(i + 2, bx, Y, bz); }
  }
  g.computeVertexNormals();
  return g;
}

function buildGoal(track) {
  const group = new THREE.Group();
  const white = track(new THREE.MeshStandardMaterial({ color: '#ffffff', roughness: 0.32, metalness: 0.05, emissive: '#1a1a1a' }));
  const support = track(new THREE.MeshStandardMaterial({ color: '#c9ccd2', roughness: 0.5, metalness: 0.4 }));
  const postGeo = track(new THREE.CylinderGeometry(GOAL.postR, GOAL.postR, BAR_Y + GOAL.postR, 20));
  for (const s of [-1, 1]) {
    const post = new THREE.Mesh(postGeo, white);
    post.position.set(s * PX, (BAR_Y + GOAL.postR) / 2, 0);
    post.castShadow = true;
    group.add(post);
  }
  const bar = new THREE.Mesh(track(new THREE.CylinderGeometry(GOAL.postR, GOAL.postR, PX * 2, 20)), white);
  bar.rotation.z = Math.PI / 2;
  bar.position.set(0, BAR_Y, 0);
  bar.castShadow = true;
  group.add(bar);

  // back frame tubes for the box net
  const tube = (a, b, r = 0.03) => {
    const len = a.distanceTo(b);
    const m = new THREE.Mesh(track(new THREE.CylinderGeometry(r, r, len, 8)), support);
    m.position.copy(a).add(b).multiplyScalar(0.5);
    m.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), b.clone().sub(a).normalize());
    m.castShadow = true;
    group.add(m);
  };
  const D = -GOAL.depth;
  const V = (x, y, z) => new THREE.Vector3(x, y, z);
  for (const s of [-1, 1]) {
    tube(V(s * PX, BAR_Y, 0), V(s * PX, BAR_Y, D));
    tube(V(s * PX, BAR_Y, D), V(s * PX, 0, D));
    tube(V(s * PX, 0.02, 0), V(s * PX, 0.02, D), 0.02);
  }
  tube(V(-PX, BAR_Y, D), V(PX, BAR_Y, D));
  tube(V(-PX, 0.02, D), V(PX, 0.02, D), 0.02);

  // nets: subdivided planes whose vertices ripple when the ball hits them
  const tex = track(netTexture());
  const nets = [];
  const mk = (id, w, h, segW, segH, position, rotation, outward, repeatW, repeatH) => {
    const t = tex.clone();
    t.repeat.set(repeatW, repeatH);
    t.needsUpdate = true;
    track(t);
    const mat = track(new THREE.MeshStandardMaterial({
      map: t, transparent: true, alphaTest: 0.08, side: THREE.DoubleSide, roughness: 0.9, depthWrite: false, color: '#f2f2f2',
    }));
    const geo = track(new THREE.PlaneGeometry(w, h, segW, segH));
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.copy(position);
    mesh.rotation.copy(rotation);
    mesh.renderOrder = 2;
    group.add(mesh);
    const base = Float32Array.from(geo.attributes.position.array);
    nets.push({ id, mesh, geo, base, outward, ripples: [], active: false });
  };
  const cell = 0.13;
  mk('back', PX * 2, BAR_Y, 36, 14, V(0, BAR_Y / 2, D), new THREE.Euler(0, 0, 0), -1, (PX * 2) / cell, BAR_Y / cell);
  mk('roof', PX * 2, GOAL.depth, 36, 10, V(0, BAR_Y, D / 2), new THREE.Euler(-Math.PI / 2, 0, 0), 1, (PX * 2) / cell, GOAL.depth / cell);
  mk('left', GOAL.depth, BAR_Y, 10, 14, V(-PX, BAR_Y / 2, D / 2), new THREE.Euler(0, Math.PI / 2, 0), -1, GOAL.depth / cell, BAR_Y / cell);
  mk('right', GOAL.depth, BAR_Y, 10, 14, V(PX, BAR_Y / 2, D / 2), new THREE.Euler(0, Math.PI / 2, 0), 1, GOAL.depth / cell, BAR_Y / cell);

  const local = new THREE.Vector3();
  return {
    group,
    nets: {
      /** Start a ripple on panel `id` at world point `p` with impact speed `speed`. */
      hit(id, p, speed) {
        const n = nets.find((x) => x.id === id);
        if (!n) return;
        n.mesh.updateMatrixWorld();
        local.copy(p);
        n.mesh.worldToLocal(local);
        n.ripples.push({ x: local.x, y: local.y, amp: Math.min(0.55, speed * 0.028), t: 0 });
        if (n.ripples.length > 4) n.ripples.shift();
        n.active = true;
      },
      reset() {
        for (const n of nets) {
          n.ripples.length = 0;
          n.geo.attributes.position.array.set(n.base);
          n.geo.attributes.position.needsUpdate = true;
          n.active = false;
        }
      },
    },
    updateNets(dt) {
      for (const n of nets) {
        if (!n.active) continue;
        const arr = n.geo.attributes.position.array;
        let alive = false;
        for (const r of n.ripples) { r.t += dt; if (r.t < 2.5) alive = true; }
        for (let i = 0; i < arr.length; i += 3) {
          const x = n.base[i];
          const y = n.base[i + 1];
          let dz = 0;
          for (const r of n.ripples) {
            const d2 = (x - r.x) ** 2 + (y - r.y) ** 2;
            const wave = Math.exp(-3.2 * r.t) * Math.cos(13 * r.t - Math.sqrt(d2) * 3);
            dz += r.amp * wave * Math.exp(-d2 / 0.6);
          }
          arr[i + 2] = n.base[i + 2] + dz * n.outward;
        }
        n.geo.attributes.position.needsUpdate = true;
        if (!alive) { n.ripples.length = 0; arr.set(n.base); n.active = false; }
      }
    },
  };
}

function buildStands(track, quality) {
  const group = new THREE.Group();
  const concrete = track(new THREE.MeshStandardMaterial({ color: '#2a2f3c', roughness: 0.9 }));
  const seatMat = track(new THREE.MeshStandardMaterial({ color: '#16295e', roughness: 0.75 }));
  const roofMat = track(new THREE.MeshStandardMaterial({ color: '#1a1d26', roughness: 0.6, metalness: 0.3, side: THREE.DoubleSide }));
  const stripMat = track(new THREE.MeshBasicMaterial({ color: '#e9f1ff', toneMapped: false }));
  const wallMat = track(new THREE.MeshStandardMaterial({ color: '#0f1a3a', roughness: 0.8 }));

  const ROWS = 30;
  const STEP_D = 0.85;
  const STEP_H = 0.48;
  const BASE_H = 1.6;

  // stair profile in (depth, height); extruded along the stand length
  const shape = new THREE.Shape();
  shape.moveTo(0, 0);
  shape.lineTo(0, BASE_H);
  for (let i = 0; i < ROWS; i++) {
    shape.lineTo(i * STEP_D, BASE_H + i * STEP_H);
    shape.lineTo((i + 1) * STEP_D, BASE_H + i * STEP_H);
  }
  shape.lineTo(ROWS * STEP_D, BASE_H + ROWS * STEP_H + 3);
  shape.lineTo(ROWS * STEP_D, 0);
  shape.lineTo(0, 0);

  // stands: [centre x, centre z, length, rotationY] - each faces the pitch
  const defs = [
    { x: 0, z: -11, len: 124, ry: 0 }, // behind the goal
    { x: -44, z: 20, len: 70, ry: Math.PI / 2 }, // left side
    { x: 44, z: 20, len: 70, ry: -Math.PI / 2 }, // right side
  ];
  const torsoMatrices = [];
  const colors = [];
  const rand = rng(99);
  const palette = ['#e8313f', '#ffffff', '#1d4fd8', '#f5c542', '#d8fd71', '#101010', '#e8313f', '#1d4fd8', '#ff7a1a', '#ffffff'];
  const m4 = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const sVec = new THREE.Vector3();
  const pVec = new THREE.Vector3();

  for (const d of defs) {
    const g = track(new THREE.ExtrudeGeometry(shape, { depth: d.len, bevelEnabled: false, steps: 1 }));
    // ExtrudeGeometry extrudes along +z; turn it so depth runs away from the pitch and length runs along x
    g.rotateY(Math.PI / 2); // shape x (depth) -> -z, extrusion z -> +x
    g.translate(-d.len / 2, 0, 0);
    const stand = new THREE.Group();
    const body = new THREE.Mesh(g, [concrete, seatMat]); // groups: 0 = end caps, 1 = stepped surface
    body.receiveShadow = false;
    stand.add(body);
    // roof + light strip
    const topY = BASE_H + ROWS * STEP_H + 3.5;
    const roof = new THREE.Mesh(track(new THREE.BoxGeometry(d.len + 2, 0.5, ROWS * STEP_D + 8)), roofMat);
    roof.position.set(0, topY + 1.2, -(ROWS * STEP_D) / 2 + 3);
    roof.rotation.x = -0.08;
    stand.add(roof);
    const strip = new THREE.Mesh(track(new THREE.BoxGeometry(d.len, 0.25, 0.1)), stripMat);
    strip.position.set(0, topY + 0.85, 7);
    stand.add(strip);
    const wall = new THREE.Mesh(track(new THREE.BoxGeometry(d.len, BASE_H, 0.3)), wallMat);
    wall.position.set(0, BASE_H / 2, 0.15);
    stand.add(wall);
    stand.position.set(d.x, 0, d.z);
    stand.rotation.y = d.ry;
    stand.updateMatrixWorld(true);
    group.add(stand);

    // crowd: one fan per seat, with gaps
    const perRow = Math.floor(d.len / 0.62);
    for (let r = 0; r < ROWS; r++) {
      for (let i = 0; i < perRow; i++) {
        if (rand() < 0.1) continue;
        const lx = -d.len / 2 + (i + 0.5) * 0.62 + (rand() - 0.5) * 0.12;
        const lz = -(r * STEP_D + STEP_D * 0.55);
        const ly = BASE_H + r * STEP_H;
        pVec.set(lx, ly, lz).applyMatrix4(stand.matrixWorld);
        const sc = 0.88 + rand() * 0.24;
        q.setFromAxisAngle(new THREE.Vector3(0, 1, 0), d.ry + (rand() - 0.5) * 0.4);
        sVec.set(sc, sc, sc);
        m4.compose(pVec, q, sVec);
        torsoMatrices.push(m4.clone());
        colors.push(new THREE.Color(palette[(rand() * palette.length) | 0]).multiplyScalar(0.32 + rand() * 0.38)); // dimmer: night match
      }
    }
  }

  // shuffle so lowering density removes fans evenly rather than whole stands
  for (let i = torsoMatrices.length - 1; i > 0; i--) {
    const j = (rand() * (i + 1)) | 0;
    [torsoMatrices[i], torsoMatrices[j]] = [torsoMatrices[j], torsoMatrices[i]];
    [colors[i], colors[j]] = [colors[j], colors[i]];
  }

  const uniforms = { uTime: { value: 0 }, uExcite: { value: 0 } };
  const bob = (mat) => {
    mat.onBeforeCompile = (shader) => {
      shader.uniforms.uTime = uniforms.uTime;
      shader.uniforms.uExcite = uniforms.uExcite;
      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', '#include <common>\nuniform float uTime; uniform float uExcite;')
        .replace('#include <begin_vertex>', `#include <begin_vertex>
          float hsh = fract(sin(float(gl_InstanceID) * 12.9898) * 43758.5453);
          float jump = abs(sin(uTime * (3.0 + hsh * 3.0) + hsh * 40.0));
          transformed.y += jump * (0.025 + uExcite * 0.32 * step(0.25, hsh));
          transformed.x += sin(uTime * 1.3 + hsh * 20.0) * 0.03;`);
    };
    return mat;
  };
  const torsoGeo = track(new THREE.CapsuleGeometry(0.17, 0.26, 1, 5)); // low-poly: fans are far away
  torsoGeo.scale(1.1, 1, 0.7);
  torsoGeo.translate(0, 0.3, 0);
  const headGeo = track(new THREE.SphereGeometry(0.115, 6, 4));
  headGeo.translate(0, 0.68, 0);
  const torsoMat = track(bob(new THREE.MeshLambertMaterial({ color: '#ffffff' })));
  const headMat = track(bob(new THREE.MeshLambertMaterial({ color: '#ffffff' })));
  const count = torsoMatrices.length;
  const torsos = new THREE.InstancedMesh(torsoGeo, torsoMat, count);
  const heads = new THREE.InstancedMesh(headGeo, headMat, count);
  const skins = ['#5a3a24', '#8d5524', '#c68642', '#e0ac69', '#f1c27d', '#3d2616', '#a16e4b'].map((c) => new THREE.Color(c));
  for (let i = 0; i < count; i++) {
    torsos.setMatrixAt(i, torsoMatrices[i]);
    heads.setMatrixAt(i, torsoMatrices[i]);
    torsos.setColorAt(i, colors[i]);
    heads.setColorAt(i, skins[i % skins.length].clone().multiplyScalar(0.75));
  }
  torsos.frustumCulled = heads.frustumCulled = false;
  group.add(torsos, heads);
  const setDensity = (f) => { torsos.count = heads.count = Math.floor(count * f); };
  setDensity(quality === 'low' ? 0.45 : 1);

  // tunnel entrances beside the goal stand, with merged steps
  const stepsGeo = mergeGeometries([new THREE.BoxGeometry(6, 0.2, 1.2), new THREE.BoxGeometry(6, 0.2, 1.2).translate(0, 0.2, -0.6)]);
  track(stepsGeo);
  for (const s of [-1, 1]) {
    // tunnel / dugout blocks beside the goal stand
    const tunnel = new THREE.Mesh(track(new THREE.BoxGeometry(6, 2.6, 3)), concrete);
    tunnel.position.set(s * 26, 1.3, -7.5);
    group.add(tunnel);
    const steps = new THREE.Mesh(stepsGeo, concrete);
    steps.position.set(s * 26, 0.1, -5.6);
    group.add(steps);
  }

  return { group, uniforms, setDensity, count };
}
